import { SubmitHelpers } from '@area2k/use-form';
import { useCallback } from 'react';

import { getJobValues } from '../../util';

import Button from '@/components/Button';
import Card from '@/components/Card';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import { MAX_LENGTH_JOB_INSTRUCTIONS } from '@/constants/text';
import Form from '@/form';
import TextAreaField from '@/form/TextAreaField';
import { useUpdateJobMutation } from '@/graphql';
import {
  GetJobQuery,
  Scalars,
  UpdateJobMutationVariables,
} from '@/types/graphql';
import { handleMutationFormError } from '@/util/error';

export type Props = {
  job: GetJobQuery['job'];
  hideModal: () => Scalars['Void'];
};

type FormValues = {
  instruction: UpdateJobMutationVariables['instructions'];
};

const UpdateInstructionModal = ({ job, hideModal }: Props) => {
  const initialValues: FormValues = {
    instruction: job.instructions ?? job.skill.descriptionEn ?? '',
  };

  const [updateJob, { loading: isLoading }] = useUpdateJobMutation({
    update: (cache) => {
      cache.modify({
        id: cache.identify(job),
        fields: {
          instructions() {},
        },
      });
    },
  });

  const handleSubmit = useCallback(
    async (values: FormValues, { setFormError }: SubmitHelpers) => {
      const jobValues = getJobValues(job);
      try {
        if (values.instruction === job.instructions) {
          return hideModal();
        }

        await updateJob({
          variables: {
            ...jobValues,
            instructions: values.instruction.trim(),
          },
        });

        hideModal();
      } catch (err) {
        setFormError('error', { message: err.message });
        handleMutationFormError(err, { setFormError });
      }
    },
    [job]
  );

  return (
    <Modal
      disableClickout
      size="sm"
      title="Change Instructions"
      onRequestClose={hideModal}
    >
      <Card.Section>
        <Form initialValues={initialValues} onSubmit={handleSubmit}>
          <TextAreaField
            required
            fieldId="instruction"
            label="Job instructions"
            maxLength={MAX_LENGTH_JOB_INSTRUCTIONS}
            placeholder="Provide any job instructions..."
          />
          <Stack justify="end">
            <Button
              a11yLabel="Submit form"
              isLoading={isLoading}
              label="Save"
              type="submit"
            />
          </Stack>
        </Form>
      </Card.Section>
    </Modal>
  );
};

export default UpdateInstructionModal;
